import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, ron, STATUS_LABELS, EXPENSE_CATEGORIES, VAT_STATUS, VAT_LABEL, DOCUMENTS_STATUS, DOCUMENTS_STATUS_MAP, formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/date";
import { displayLei, formatLei } from "@/lib/money";
import StatusBadge from "@/components/StatusBadge";
import CarDocSlot from "@/components/CarDocSlot";
import MarkSoldDialog from "@/components/MarkSoldDialog";
import DualMoneyInput from "@/components/DualMoneyInput";
import { RateBadge } from "@/components/RateBadge";
import DateInput from "@/components/DateInput";
import { useCurrency } from "@/context/CurrencyContext";
import { ArrowLeft, Receipt, Trash2, Save, Trophy, Paperclip, Download, FileCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-400">{label}</div>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

function LeiHint({ amount, expense }) {
  const { rate } = useCurrency();
  // If we have the original expense doc with original_currency, use locked lei
  if (expense && expense.original_currency === "RON" && expense.original_amount != null) {
    return <span className="text-[10px] text-slate-400 tabular block leading-none mt-0.5">{formatLei(expense.original_amount)} (fix)</span>;
  }
  const r = expense?.rate_locked && expense?.rate_at_entry ? expense.rate_at_entry : rate;
  if (!amount || !r) return null;
  const lei = Math.round(Number(amount) * r);
  return <span className="text-[10px] text-slate-400 tabular block leading-none mt-0.5">≈ {new Intl.NumberFormat("ro-RO").format(lei)} lei</span>;
}

export default function CarDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [car, setCar] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [newExp, setNewExp] = useState({ category: "Reparații", amount: "", description: "", invoice_path: null, invoice_filename: "", vat_status: "cu_tva", original_amount: null, original_currency: null, rate_at_entry: null });
  const [soldOpen, setSoldOpen] = useState(false);
  const invoiceRef = useRef();

  async function load() {
    try {
      const { data } = await api.get(`/cars/${id}`);
      setCar(data);
      const ex = await api.get(`/expenses`, { params: { car_id: id } });
      setExpenses(ex.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("data:refresh", h);
    return () => window.removeEventListener("data:refresh", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function update(patch) {
    try {
      const body = {
        make: car.make, model: car.model, year: car.year, vin: car.vin,
        color: car.color, mileage: car.mileage,
        fuel: car.fuel, transmission: car.transmission,
        purchase_price: Number(car.purchase_price) || 0,
        asking_price: Number(car.asking_price) || 0,
        status: car.status, notes: car.notes, photos: car.photos || [],
        purchase_date: car.purchase_date,
        carvertical_doc: car.carvertical_doc || null,
        car_documents_doc: car.car_documents_doc || null,
        cmr_doc: car.cmr_doc || null,
        vat_deductible: !!car.vat_deductible,
        documents_status: car.documents_status || "pending",
        ...patch,
      };
      const { data } = await api.put(`/cars/${id}`, body);
      setCar(data);
      toast.success("Salvat");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  async function pickInvoice(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/files/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setNewExp({ ...newExp, invoice_path: data.path, invoice_filename: data.filename });
      toast.success("Factură atașată");
    } catch {
      toast.error("Upload eșuat");
    } finally {
      e.target.value = "";
    }
  }

  async function downloadInvoice(path, filename) {
    try {
      const r = await api.get("/files/serve", { params: { path }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename || "factura.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error("Descărcare eșuată"); }
  }

  async function addExpense(e) {
    e.preventDefault();
    try {
      await api.post(`/expenses`, {
        car_id: id,
        category: newExp.category,
        amount: Number(newExp.amount),
        description: newExp.description,
        invoice_path: newExp.invoice_path,
        vat_status: newExp.vat_status,
        original_amount: newExp.original_amount,
        original_currency: newExp.original_currency,
        rate_at_entry: newExp.rate_at_entry,
        rate_locked: !!newExp.rate_locked,
      });
      setNewExp({ category: "Reparații", amount: "", description: "", invoice_path: null, invoice_filename: "", vat_status: "cu_tva", original_amount: null, original_currency: null, rate_at_entry: null });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  async function removeExpense(eid) {
    await api.delete(`/expenses/${eid}`);
    load();
  }

  async function deleteCar() {
    if (!window.confirm("Ștergi mașina și toate cheltuielile asociate?")) return;
    await api.delete(`/cars/${id}`);
    navigate("/cars");
  }

  if (!car) return <div className="sm:pl-52 text-slate-400">Se încarcă...</div>;

  const profitColor = car.profit_estimated >= 0 ? "from-emerald-500 to-emerald-600" : "from-red-500 to-red-600";

  return (
    <div className="sm:pl-52 space-y-5" data-testid="car-detail-page">
      <Link to="/cars" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" /> Înapoi la stoc
      </Link>

      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-slate-400">{car.year || "—"}</div>
            <h1 className="font-display text-3xl tracking-tight font-medium">{car.make} {car.model}</h1>
            {car.vin && <div className="text-xs font-mono text-slate-500 mt-1 break-all">VIN: {car.vin}</div>}
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                data-testid="vat-toggle"
                type="button"
                onClick={() => update({ vat_deductible: !car.vat_deductible })}
                className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-medium px-2.5 py-1 rounded-full border transition-colors ${
                  car.vat_deductible
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                    : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"
                }`}
                title="Click pentru a comuta TVA deductibil/nedeductibil"
              >
                <span className={`w-1.5 h-1.5 rounded-full ${car.vat_deductible ? "bg-emerald-500" : "bg-slate-400"}`} />
                TVA {car.vat_deductible ? "deductibil" : "nedeductibil"}
              </button>
              {/* Documents status segmented control */}
              <div className="inline-flex items-center bg-slate-50 border border-slate-200 rounded-full p-0.5" role="group" aria-label="Status acte mașină">
                {DOCUMENTS_STATUS.map((d) => {
                  const active = (car.documents_status || "pending") === d.value;
                  return (
                    <button
                      key={d.value}
                      data-testid={`docs-status-${d.value}`}
                      type="button"
                      onClick={() => !active && update({ documents_status: d.value })}
                      title={d.label}
                      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-medium px-2.5 py-1 rounded-full transition-all ${
                        active
                          ? `${d.bg} ${d.text} shadow-sm`
                          : "text-slate-500 hover:text-slate-800"
                      }`}
                    >
                      <span>{d.emoji}</span> {d.short}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              data-testid="car-detail-status"
              value={car.status}
              onChange={(e) => update({ status: e.target.value })}
              className="bg-white border border-slate-200 rounded-full px-3 py-1.5 text-sm"
            >
              {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            {car.status !== "vanduta" && (
              <button
                data-testid="open-mark-sold"
                onClick={() => setSoldOpen(true)}
                className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm px-3 py-1.5 rounded-full"
              >
                <Trophy className="w-3.5 h-3.5" /> Marchează vândut
              </button>
            )}
          </div>
        </div>

        {/* Big profit card */}
        <div className={`mt-5 p-5 rounded-2xl bg-gradient-to-br ${profitColor} text-white`} data-testid="big-profit-card">
          <div className="text-[10px] uppercase tracking-widest text-white/70">Profit estimat</div>
          <div className="font-display text-4xl sm:text-5xl tracking-tight font-semibold tabular mt-1">{ron(car.profit_estimated)}</div>
          <div className="text-xs text-white/80 mt-1">
            Investiție {ron(car.investment_total)} → preț cerut {ron(car.asking_price)}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 pt-4 border-t border-slate-100">
          <Field label="Preț achiziție"><div className="font-display text-lg font-medium tabular">{ron(car.purchase_price)}</div></Field>
          <Field label="Cheltuieli">
            <div className="font-display text-lg font-medium tabular">{ron(car.expenses_total)}</div>
            <div className="text-[10px] text-slate-400">auto-însumate</div>
          </Field>
          <Field label="Preț cerut"><div className="font-display text-lg font-medium tabular">{ron(car.asking_price)}</div></Field>
          <Field label="Zile în stoc"><div className={`font-display text-lg font-medium tabular ${car.days_in_stock >= 90 ? "text-red-600" : car.days_in_stock >= 60 ? "text-amber-600" : ""}`}>{car.days_in_stock}</div></Field>
        </div>
      </div>

      {/* Quick action: Add expense (sticky CTA) */}
      <div className="bg-[#0A0F1C] text-white rounded-2xl p-4 flex items-center justify-between gap-3" data-testid="quick-add-expense-bar">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-white/60">Acțiune rapidă</div>
          <div className="font-display text-base font-medium">Adaugă cheltuială pentru această mașină</div>
        </div>
        <button
          data-testid="scroll-to-expense"
          onClick={() => document.getElementById("expense-section")?.scrollIntoView({ behavior: "smooth" })}
          className="shrink-0 whitespace-nowrap bg-[#0047FF] hover:bg-[#003BD3] text-white px-3 py-2 rounded-lg text-sm"
        >
          + Cheltuială
        </button>
      </div>

      {/* Documents (PDFs) */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Documente</div>
        <h2 className="font-display text-lg font-medium mb-3 flex items-center gap-2"><FileCheck className="w-4 h-4" />Acte mașină</h2>
        <div className="space-y-2">
          <CarDocSlot
            testid="doc-carvertical"
            label="Raport Carvertical"
            value={car.carvertical_doc}
            onChange={(v) => update({ carvertical_doc: v })}
          />
          <CarDocSlot
            testid="doc-documente"
            label="Documente mașină"
            value={car.car_documents_doc}
            onChange={(v) => update({ car_documents_doc: v })}
          />
          <CarDocSlot
            testid="doc-cmr"
            label="CMR"
            value={car.cmr_doc}
            onChange={(v) => update({ cmr_doc: v })}
          />
        </div>
      </div>

      {/* Details edit */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-3">Detalii</div>
        <div className="grid grid-cols-2 gap-3">
          {[
            ["Marcă", "make"], ["Model", "model"],
            ["VIN", "vin"], ["An", "year", "number"],
            ["Data achiziției", "purchase_date", "date"],
            ["Culoare", "color"], ["Km", "mileage", "number"], ["Combustibil", "fuel"],
            ["Cutie", "transmission"], ["Preț achiziție (€)", "purchase_price", "number"],
            ["Preț vânzare (€)", "asking_price", "number"],
          ].map(([label, key, type]) => (
            <div key={key}>
              <label className="text-[10px] uppercase tracking-widest text-slate-400">{label}</label>
              {type === "date" ? (
                <div className="mt-0.5">
                  <DateInput
                    testid={`car-edit-${key}`}
                    value={(car[key] || "").slice(0, 10)}
                    onChange={(v) => setCar({ ...car, [key]: v })}
                  />
                </div>
              ) : (
                <input
                  data-testid={`car-edit-${key}`}
                  type={type || "text"}
                  value={car[key] ?? ""}
                  onChange={(e) => setCar({ ...car, [key]: e.target.value })}
                  className="mt-0.5 w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0047FF]"
                />
              )}
            </div>
          ))}
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-slate-400">Notițe</label>
            <textarea
              data-testid="car-edit-notes"
              value={car.notes || ""}
              onChange={(e) => setCar({ ...car, notes: e.target.value })}
              rows={3}
              className="mt-0.5 w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0047FF]"
            />
          </div>
        </div>
        <div className="mt-3 flex justify-between">
          <button data-testid="delete-car-btn" onClick={deleteCar} className="text-red-600 text-sm inline-flex items-center gap-1.5 hover:underline">
            <Trash2 className="w-4 h-4" /> Șterge mașina
          </button>
          <button data-testid="car-save-btn" onClick={() => update({})} className="bg-[#0047FF] hover:bg-[#003BD3] text-white px-4 py-2 rounded-lg text-sm inline-flex items-center gap-2">
            <Save className="w-4 h-4" /> Salvează
          </button>
        </div>
      </div>

      {/* Expenses */}
      <div id="expense-section" className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400">Costuri</div>
            <h2 className="font-display text-lg font-medium flex items-center gap-2"><Receipt className="w-4 h-4" />Cheltuieli ({expenses.length})</h2>
          </div>
          <div className="text-sm tabular">Total: <strong>{ron(expenses.reduce((s, e) => s + (e.amount || 0), 0))}</strong></div>
        </div>

        <form onSubmit={addExpense} className="space-y-2 mb-3">
          <div className="grid grid-cols-12 gap-2">
            <select data-testid="exp-cat-input" value={newExp.category} onChange={(e) => setNewExp({ ...newExp, category: e.target.value })} className="col-span-12 sm:col-span-3 border border-slate-200 rounded-lg px-2 py-2 text-sm">
              {EXPENSE_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <div className="col-span-12 sm:col-span-5">
              <DualMoneyInput
                testid="exp-money"
                value={newExp.amount}
                onChange={(v) => setNewExp({ ...newExp, amount: v })}
                onMeta={(m) => setNewExp((prev) => ({ ...prev, ...m }))}
              />
            </div>
            <input data-testid="exp-desc-input" placeholder="Descriere" value={newExp.description} onChange={(e) => setNewExp({ ...newExp, description: e.target.value })} className="col-span-8 sm:col-span-2 border border-slate-200 rounded-lg px-2 py-2 text-sm" />
            <button data-testid="exp-add-btn" type="submit" className="col-span-4 sm:col-span-2 bg-[#0A0F1C] text-white rounded-lg text-sm py-2">Adaugă</button>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select data-testid="exp-vat-input" value={newExp.vat_status} onChange={(e) => setNewExp({ ...newExp, vat_status: e.target.value })} className="border border-slate-200 rounded-full px-3 py-1.5 text-xs bg-white">
              {VAT_STATUS.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
            </select>
            <button type="button" data-testid="attach-invoice-btn" onClick={() => invoiceRef.current?.click()} className="inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-[#0047FF] border border-slate-200 rounded-full px-3 py-1.5">
              <Paperclip className="w-3.5 h-3.5" /> {newExp.invoice_path ? "Schimbă factura" : "Atașează factură (PDF/foto)"}
            </button>
            {newExp.invoice_filename && (
              <span className="text-xs text-emerald-700 truncate">✓ {newExp.invoice_filename}</span>
            )}
            <input ref={invoiceRef} type="file" accept="application/pdf,image/*" capture="environment" className="hidden" onChange={pickInvoice} />
          </div>
        </form>

        <div className="divide-y divide-slate-100">
          {expenses.map((e) => (
            <div key={e.id} data-testid={`expense-row-${e.id}`} className="flex items-center justify-between py-2">
              <div className="min-w-0">
                <div className="text-sm font-medium flex items-center gap-2">
                  {e.category}
                  {e.invoice_path && <span title="Are factură" className="text-emerald-600"><Paperclip className="w-3 h-3" /></span>}
                  {e.vat_status && (
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                      e.vat_status === "cu_tva" ? "bg-blue-50 text-blue-700"
                      : e.vat_status === "fara_tva" ? "bg-amber-50 text-amber-700"
                      : "bg-slate-100 text-slate-600"
                    }`}>{VAT_LABEL[e.vat_status]}</span>
                  )}
                </div>
                <div className="text-xs text-slate-500 truncate">{e.description || "—"} · {formatDate(e.date)}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {e.invoice_path && (
                  <button data-testid={`download-invoice-${e.id}`} onClick={() => downloadInvoice(e.invoice_path, "factura")} className="text-slate-400 hover:text-[#0047FF]">
                    <Download className="w-4 h-4" />
                  </button>
                )}
                <div className="text-right">
                  <span className="inline-flex items-center gap-1.5"><RateBadge doc={e} testid={`expense-rate-badge-${e.id}`} /><span className="tabular text-sm font-medium">{ron(e.amount)}</span></span>
                  <LeiHint amount={e.amount} expense={e} />
                </div>
                <button onClick={() => removeExpense(e.id)} className={`text-slate-400 hover:text-red-600 ${isAdmin ? "" : "hidden"}`} data-testid={`delete-expense-${e.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          {expenses.length === 0 && <div className="text-sm text-slate-400 py-3">Nicio cheltuială înregistrată.</div>}
        </div>
      </div>

      <MarkSoldDialog open={soldOpen} onClose={() => setSoldOpen(false)} car={car} onSold={() => { load(); navigate("/sales"); }} />
    </div>
  );
}
